import * as z from "zod";
import {
  FlowContentNodeSchema,
  type FlowContentNode,
} from "./flow-content-node-schema";

// =============================================================================
// FLOW CONTENT NODE - Generic type for any node in the content tree
// =============================================================================

/**
 * Flow content JSON - the root structure containing an array of nodes.
 */
const FlowContentSchema_ = z.object({
  content: z.array(FlowContentNodeSchema),
});
export const FlowContentSchema: z.ZodType<FlowContent> = FlowContentSchema_;
export interface FlowContent extends z.infer<typeof FlowContentSchema_> {}

/**
 * Generic type guard to check if a node is a specific type.
 */
export function isNodeType<T extends string>(
  node: unknown,
  type: T,
): node is FlowContentNode & { type: T } {
  return (
    typeof node === "object" &&
    node !== null &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (node as Record<string, unknown>).type === type
  );
}

// =============================================================================
// LOCK TYPES
// =============================================================================

export {
  BlockLockAttrsSchema,
  BranchLockAttrsSchema,
  ConditionalLockAttrsSchema,
  InlineLockAttrsSchema,
  SectionLockAttrsSchema,
  StepLockAttrsSchema,
  type BlockLockAttrs,
  type BranchLockAttrs,
  type ConditionalLockAttrs,
  type InlineLockAttrs,
  type SectionLockAttrs,
  type StepLockAttrs,
} from "./lock-types-schema";

// =============================================================================
// NODE SCHEMAS AND TYPE GUARDS
// =============================================================================

// Text node
export { TextNodeSchema, type TextNode } from "./text-node-schema";

// Variable node
export {
  isVariableNode,
  VariableNodeSchema,
  type VariableNode,
  type VariableNodeAttrs,
  type VariableNodeBase,
} from "./variable-node-schema";

// Mention node
export {
  MentionNodeSchema,
  type MentionNode,
  type MentionNodeAttrs,
  type MentionNodeBase,
} from "./mention-node-schema";

// Tool node
export {
  isToolNode,
  ToolNodeSchema,
  type ToolNode,
  type ToolNodeAttrs,
} from "./tool-node-schema";

// Heading node
export { HeadingNodeSchema, type HeadingNode } from "./heading-node-schema";

// Paragraph node
export {
  ParagraphNodeSchema,
  type ParagraphNode,
} from "./paragraph-node-schema";

// Newline node
export { NewlineNodeSchema, type NewlineNode } from "./newline-node-schema";

// List nodes
export {
  BulletListNodeSchema,
  ListItemNodeSchema,
  OrderedListNodeSchema,
  type BulletListNode,
  type ListItemNode,
  type OrderedListNode,
} from "./list-node-schema";

// Horizontal rule node
export {
  HorizontalRuleNodeSchema,
  type HorizontalRuleNode,
} from "./horizontal-rule-node-schema";

// =============================================================================
// CONDITIONAL NODE
// =============================================================================

export {
  ConditionContentItemSchema,
  ConditionSchema,
  conditionTypes,
  LOGIC_OPERATORS,
  logicOperators,
  type Condition,
  type ConditionContentItem,
  type ConditionType,
  type LogicOperator,
} from "./conditional/condition-schema";

export {
  branchTypes,
  ConditionalBranchSchema,
  ConditionalNodeSchema,
  ConditionalStepSchema,
  isConditionalNode,
  type BranchType,
  type ConditionalBranch,
  type ConditionalNode,
  type ConditionalStep,
} from "./conditional/conditional-node-schema";

// =============================================================================
// SECTION NODE
// =============================================================================

export {
  isSectionNode,
  SectionNodeSchema,
  type SectionNode,
} from "./section/section-node-schema";

// =============================================================================
// STEP SELECT NODE
// =============================================================================

export {
  isStepSelectNode,
  StepSelectNodeSchema,
  type StepSelectNode,
  type StepSelectNodeAttrs,
  type StepSelectNodeBase,
} from "./step-select-node-schema";

// =============================================================================
// EXAMPLE NODE
// =============================================================================

export {
  createDefaultExampleNodeAttrs,
  ExampleNodeSchema,
  isExampleNode,
  type ExampleChannel,
  type ExampleChannelsContent,
  type ExampleNode,
  type ExampleNodeAttrs,
  type ExampleNodeBase,
} from "./example";

export {
  FlowContentNodeSchema,
  type FlowContentNode,
} from "./flow-content-node-schema";

// =============================================================================
// INTERPRETER NODE TYPES
// =============================================================================

export {
  InterpreterNodeSchema,
  interpreterNodeTypes,
  isInterpreterNodeType,
  isStepInputType,
  stepInputTypes,
  validateInterpreterNode,
  type InterpreterNode,
  type InterpreterNodeType,
  type StepInputType,
} from "./interpreter-nodes";
